import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import {
  Form,
  Row,
  Col,
  Select,
  Input,
  message,
  Modal,
  InputNumber,
} from 'antd';
import { OmsRecycleOrderDetail, OmsConsignmentToRecycling } from 'types/oms';
import { consignmentToRecycling } from 'apis/oms';
import i18n from 'i18next';
import { thousands } from 'utils/tools';
import { PaymentMethod } from 'constants/RecyclingConsignment';
import LOCALS from 'commons/locals';

interface Props {
  open: boolean;
  onClose: () => void;
  data: OmsRecycleOrderDetail;
  reload: () => void;
}

export const getDefaultSettlementTypeByCurrency = (currency?: string) => {
  return currency === 'HKD' ? 2 : 1;
};

const ConsignmentToRecyclingModal = ({
  open,
  onClose,
  data,
  reload,
}: Props) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const currency = useMemo(() => {
    return data?.omsRecycleOrder?.currency;
  }, [data]);
  const defaultSettlementType = useMemo(() => {
    return getDefaultSettlementTypeByCurrency(currency);
  }, [currency]);

  useEffect(() => {
    form.setFieldsValue({
      id: data?.omsRecycleOrder?.id,
      recycleValue: data?.omsRecycleOrderItem?.finalRecyclePrice,
      settlementType: defaultSettlementType,
    });
  }, [defaultSettlementType, form, data]);

  const onFinish = useCallback(() => {
    form.validateFields().then((values) => {
      const payload: OmsConsignmentToRecycling = {
        id: values.id,
        settlementType: values.settlementType,
        finalRecyclePrice: values.recycleValue,
      };

      setSubmitting(true);
      consignmentToRecycling(payload)
        .then(() => {
          message.success(i18n.t('successful_operation'));
          onClose();
          reload();
        })
        .finally(() => {
          setSubmitting(false);
        });
    });
  }, [form, onClose, reload]);

  return (
    <Modal
      open={open}
      title={i18n.t('consignment_to_recycling')}
      onCancel={() => onClose()}
      onOk={onFinish}
      confirmLoading={submitting}
      className={styles.PayInfoModal}
      destroyOnClose
      width={700}
    >
      <div className="text-lg text-red-500 mb-4">
        {i18n.t('consignment_to_recycling_tips')}
      </div>
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        initialValues={{
          settlementType: defaultSettlementType,
        }}
        labelWrap
      >
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
        <Row gutter={[48, 0]}>
          <Col span={24}>
            <Form.Item
              label={i18n.t('recycling_confirmation_quote')}
              name="recycleValue"
              rules={[{ required: true }]}
              className="mb-0"
            >
              <InputNumber
                inputMode="numeric"
                max={1000000000}
                addonBefore={currency}
                style={{ width: '100%' }}
                min={1}
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>
            <Form.Item colon={false} label={<div></div>}>
              {i18n.t('consignment_confirmation_quote')}：
              {thousands(data?.omsRecycleOrderItem?.finalSalePrice)}
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label={i18n.t(LOCALS.payment_method)}
          name="settlementType"
          rules={[{ required: true }]}
        >
          <Select options={PaymentMethod} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ConsignmentToRecyclingModal;
